/**
 * MitigationAttestationBoard — the CPA's evidence-gated hand on the mediation board.
 *
 * CONCEDE (p=1, with a document citation) or clear (p=0) each of the 5 CP 00 30
 * failure-to-mitigate levers. Conceding a factor certifies the carrier's failure-to-
 * mitigate defense on it is proven → it moves the neutral E[X] = N − Σ pᵢ·Cᵢ DOWN by
 * that lever's engine Cᵢ. The user-facing verb is "Concede", NOT "Attest" — so nobody
 * reads the E[X]-dropping action as a pro-insured attestation (owner ruling 2026-09-21;
 * same PATCH the carrier-ledger exposure ladder's Concede fires). NO dollar entry (Cᵢ is
 * engine-owned, read-only); NO probability slider (on/off only); the document citation IS
 * the gate. The board number is READ from the brief (getBrief → mediator_view), never
 * recomputed here.
 *
 * K3 design gate 2026-09-10 (the "Attestation Board" spec, relabeled to "Concession
 * Board"); writes via the interpretation_object PATCH (backend claims_api.py). Mounts in
 * the accountant Engines stage, below the mitigation engine output (EngineDetailViews).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getBrief, MediatorDefenseFactor } from '../../../services/deliberationApi';
import {
  getInterpretation,
  getInterpretationCandidates,
  patchMitigationAttestation,
  CandidateCite,
  InterpMitigationLever,
  MitigationFactorId,
} from '../../../services/interpretationApi';

interface Props {
  reportId: string;
  className?: string;
}

const ARM_SECONDS = 6;
const EMPTY_FIELDS = { docId: '', page: '', quote: '' };

/** The api client throws an Error whose `.message` is the JSON-stringified FastAPI
 * `detail`. Pull a human string out of it (mirrors ExtractionCertifyControl). */
function serverMessage(err: unknown): string {
  const raw = (err as { message?: string })?.message || 'Request failed';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return (parsed.message as string) || (parsed.detail as string) || raw;
    }
  } catch {
    /* not JSON — use as-is */
  }
  return raw;
}

function fmtMoney(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Middle-truncate a filename so the traceable tail (case/exhibit id + extension) survives
 * — end-truncation ("Extra_Expense_Compilati…") drops exactly the part that identifies the
 * exhibit (K3 gate). e.g. "…HFS-TX-2023-88214.pdf" is kept. */
function midTruncate(name: string, max = 34): string {
  if (!name || name.length <= max) return name;
  const head = Math.ceil((max - 1) * 0.5);
  const tail = Math.floor((max - 1) * 0.5);
  return `${name.slice(0, head)}…${name.slice(name.length - tail)}`;
}

const MONO = 'font-mono text-xs uppercase tracking-wider';

type ArmKind = 'attest' | 'clear';

export default function MitigationAttestationBoard({ reportId, className }: Props) {
  const [factors, setFactors] = useState<MediatorDefenseFactor[]>([]);
  const [nValue, setNValue] = useState<number | null>(null);
  const [exValue, setExValue] = useState<number | null>(null);
  const [interpLevers, setInterpLevers] = useState<Record<string, InterpMitigationLever>>({});
  const [hasView, setHasView] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openKey, setOpenKey] = useState<MitigationFactorId | null>(null);
  const [fields, setFields] = useState<{ docId: string; page: string; quote: string }>(EMPTY_FIELDS);
  const [armed, setArmed] = useState<{ key: string; kind: ArmKind } | null>(null);
  const [countdown, setCountdown] = useState<number>(ARM_SECONDS);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [quoteOpen, setQuoteOpen] = useState<Record<string, boolean>>({});
  // candidate-cite read queue (READ-ONLY; never sets p). Keyed by factor id.
  const [candidates, setCandidates] = useState<Record<string, CandidateCite[]>>({});
  const [queueOpen, setQueueOpen] = useState<Record<string, boolean>>({});
  const [prefillNote, setPrefillNote] = useState<string | null>(null);
  const [candQuoteOpen, setCandQuoteOpen] = useState<Record<string, boolean>>({});

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };
  const disarm = useCallback(() => {
    clearTimer();
    setArmed(null);
    setCountdown(ARM_SECONDS);
  }, []);
  const arm = useCallback((key: string, kind: ArmKind) => {
    clearTimer();
    setArmed({ key, kind });
    setCountdown(ARM_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearTimer();
          setArmed(null);
          return ARM_SECONDS;
        }
        return c - 1;
      });
    }, 1000);
  }, []);
  useEffect(() => () => clearTimer(), []);

  // Esc disarms whatever is armed.
  useEffect(() => {
    if (!armed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') disarm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [armed, disarm]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [briefRes, interpRes, candRes] = await Promise.all([
        getBrief(reportId).catch((e: unknown) => {
          if ((e as { status?: number })?.status === 404) return null;
          throw e;
        }),
        getInterpretation(reportId).catch(() => null),
        // read queue is enrichment — its failure never blocks the board (honest empty).
        getInterpretationCandidates(reportId).catch(() => null),
      ]);
      const mv = briefRes?.brief?.mediator_view ?? null;
      setHasView(!!mv);
      setFactors(mv?.defense_itemization ?? []);
      setNValue(mv?.blocks?.plaintiff?.value ?? null);
      setExValue(mv?.blocks?.neutral?.value ?? null);
      const levers: Record<string, InterpMitigationLever> = {};
      interpRes?.interpretation?.mitigation?.forEach((l) => {
        levers[l.id] = l;
      });
      setInterpLevers(levers);
      const cands: Record<string, CandidateCite[]> = {};
      candRes?.by_factor?.forEach((row) => {
        cands[row.factor_id] = row.candidates ?? [];
      });
      setCandidates(cands);
    } catch (e: unknown) {
      setError(serverMessage(e));
      setHasView(false);
    } finally {
      setLoading(false);
    }
  }, [reportId]);
  useEffect(() => {
    load();
  }, [load]);

  const openComposer = (key: MitigationFactorId) => {
    // single-composer rule: opening one discards any other's uncommitted fields.
    disarm();
    setComposerError(null);
    setPrefillNote(null);
    setFields(EMPTY_FIELDS);
    setOpenKey(key);
  };
  const cancelComposer = () => {
    disarm();
    setComposerError(null);
    setPrefillNote(null);
    setFields(EMPTY_FIELDS);
    setOpenKey(null);
  };

  // Read-queue prefill: pull a staged candidate's doc + verbatim quote into the
  // composer. Page is LEFT BLANK on purpose — the candidate carries `page: null`
  // (confirm-on-open); the accountant opens the doc and types the real page. This
  // only pre-fills the form; nothing is attested and no `p` is set until the human
  // arms + confirms below.
  const useCandidate = (key: MitigationFactorId, cite: CandidateCite) => {
    disarm();
    setComposerError(null);
    setFields({ docId: cite.doc_id, page: '', quote: cite.quote || '' });
    setPrefillNote(cite.source_label || cite.doc_id);
    setOpenKey(key);
  };

  const fieldErrors = (() => {
    const errs: { docId?: string; page?: string } = {};
    if (!fields.docId.trim()) errs.docId = 'DOC ID REQUIRED';
    const pg = parseInt(fields.page, 10);
    if (!Number.isInteger(pg) || pg < 1 || String(pg) !== fields.page.trim()) errs.page = 'PAGE REQUIRED';
    return errs;
  })();
  const composerValid = !fieldErrors.docId && !fieldErrors.page;

  const commitAttest = async (key: MitigationFactorId) => {
    setBusyKey(key);
    setComposerError(null);
    try {
      await patchMitigationAttestation(reportId, {
        id: key,
        p: 1,
        evidence_cite: [
          {
            doc_id: fields.docId.trim(),
            page: parseInt(fields.page, 10),
            quote: fields.quote.trim() || undefined,
          },
        ],
        attestor_role: 'accountant',
      });
      cancelComposer();
      await load();
    } catch (e: unknown) {
      setComposerError(serverMessage(e));
      disarm();
    } finally {
      setBusyKey(null);
    }
  };

  const commitClear = async (key: MitigationFactorId) => {
    setBusyKey(key);
    setRowError((r) => ({ ...r, [key]: '' }));
    try {
      await patchMitigationAttestation(reportId, { id: key, p: 0, evidence_cite: [] });
      disarm();
      await load();
    } catch (e: unknown) {
      setRowError((r) => ({ ...r, [key]: serverMessage(e) }));
      disarm();
    } finally {
      setBusyKey(null);
    }
  };

  const handleArmedButton = (key: MitigationFactorId, kind: ArmKind) => {
    const isArmed = armed?.key === key && armed?.kind === kind;
    if (!isArmed) {
      arm(key, kind);
      return;
    }
    if (kind === 'attest') commitAttest(key);
    else commitClear(key);
  };

  // ── derived header numbers (read from the brief; never recomputed as truth) ──
  const attestedCount = factors.filter((f) => (f.p ?? 0) >= 1).length;
  const proven = nValue != null && exValue != null ? nValue - exValue : null;

  const cardStyle: React.CSSProperties = {
    border: '1px solid var(--color-border)',
    background: 'var(--color-surface, var(--color-bg, #0a0a0a))',
  };

  // ── shells for loading / empty / error ──────────────────────────────────────
  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className={className}>
      <div className="rounded-[2px]" style={cardStyle} data-testid="mitigation-attestation-board">
        <div className="px-5 pt-4 pb-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between">
            <span className={MONO} style={{ color: 'var(--color-text-muted)' }}>
              CP 00 30 · FAILURE-TO-MITIGATE
            </span>
          </div>
          <div className="mt-1 text-xl" style={{ fontFamily: "'DM Serif Display', serif", color: 'var(--color-text-primary)' }}>
            Concession Board
          </div>
          {children}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className={`mt-3 ${MONO}`} style={{ color: 'var(--color-text-muted)' }}>
          LOADING MEDIATION VIEW…
        </div>
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell>
        <div className={`mt-3 ${MONO}`} style={{ color: 'var(--color-blocker)' }}>
          {error}
        </div>
      </Shell>
    );
  }
  if (!hasView) {
    return (
      <Shell>
        <div className="mt-3 text-xs" style={{ color: 'var(--color-text-muted)', fontFamily: "'DM Mono', monospace" }}>
          NO MEDIATION VIEW ON RECORD YET — RUN THE DELIBERATION TO POPULATE THE LEVERS.
        </div>
      </Shell>
    );
  }

  return (
    <div className={className}>
      <div className="rounded-[2px]" style={cardStyle} data-testid="mitigation-attestation-board">
        {/* ── A. Header ── */}
        <div className="px-5 pt-4 pb-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between">
            <span className={MONO} style={{ color: 'var(--color-text-muted)' }}>
              CP 00 30 · FAILURE-TO-MITIGATE
            </span>
            <span className="font-mono text-xs tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              {attestedCount} / {factors.length} CONCEDED
            </span>
          </div>
          <div className="mt-1 text-xl" style={{ fontFamily: "'DM Serif Display', serif", color: 'var(--color-text-primary)' }}>
            Concession Board
          </div>

          {/* ledger strip */}
          <div className="mt-3 grid grid-cols-3">
            <div>
              <div className={MONO} style={{ color: 'var(--color-text-muted)' }}>SUPPORTED NET CLAIM (N)</div>
              <div className="mt-1 text-sm text-right font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                {nValue != null ? fmtMoney(nValue) : '—'}
              </div>
            </div>
            <div className="pl-4" style={{ borderLeft: '1px solid var(--color-border)' }}>
              <div className={MONO} style={{ color: 'var(--color-text-muted)' }}>PROVEN DEDUCTIONS Σ pᵢ·Cᵢ</div>
              <div className="mt-1 text-sm text-right font-mono tabular-nums" style={{ color: proven && proven > 0 ? 'var(--color-blocker)' : 'var(--color-text-muted)' }}>
                {proven && proven > 0 ? `(${fmtMoney(proven)})` : '—'}
              </div>
            </div>
            <div className="pl-4" style={{ borderLeft: '1px solid var(--color-border)' }}>
              <div className={MONO} style={{ color: 'var(--color-text-muted)' }}>NEUTRAL E[X]</div>
              <div className="mt-1 text-2xl text-right font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {exValue != null ? fmtMoney(exValue) : '—'}
              </div>
            </div>
          </div>
          <div className="mt-2 text-xs" style={{ color: 'var(--color-text-muted)', fontFamily: "'DM Mono', monospace" }}>
            E[X] = N − Σ pᵢ·Cᵢ · p ∈ {'{0,1}'} · NO PARTIAL WEIGHTS
          </div>
        </div>

        {/* ── B. Lever list ── */}
        <div>
          {factors.map((f) => {
            const key = f.key as MitigationFactorId;
            const ci = f.engine_delta ?? 0;
            const attested = (f.p ?? 0) >= 1;
            const isComposerOpen = openKey === key;
            const attestArmed = armed?.key === key && armed?.kind === 'attest';
            const clearArmed = armed?.key === key && armed?.kind === 'clear';
            const busy = busyKey === key;
            const lever = interpLevers[key];
            const cite = lever?.evidence_cite?.[0];
            const stipulated = lever?.attestor?.role === 'stipulated_order';
            const rowCandidates = candidates[key] ?? [];
            const queueIsOpen = !!queueOpen[key];

            return (
              <div key={key} className="px-5 py-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0,1fr) 10rem 9rem', alignItems: 'start' }}>
                  {/* Col 1 — identity + (unproven) burden / (attested) seal + cite */}
                  <div className="min-w-0">
                    {/* id as a mono overline above the title — single line, no wrap (K3 nit 4) */}
                    <div className="font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>{key}</div>
                    <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{f.label}</div>

                    {!attested && (
                      <div
                        className="mt-2 rounded-[2px] px-2 py-1.5"
                        style={{ background: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}
                      >
                        <div className={MONO} style={{ color: 'var(--color-blocker)' }}>CARRIER MUST PROVE</div>
                        <div className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {f.carrier_must_prove && f.carrier_must_prove.trim()
                            ? f.carrier_must_prove
                            : <span style={{ color: 'var(--color-text-muted)' }} className={MONO}>NO BURDEN STATEMENT ON RECORD</span>}
                        </div>
                      </div>
                    )}

                    {/* ── Read queue: staged candidate cites (READ-ONLY; never sets p) ── */}
                    {!attested && (
                      <div className="mt-2">
                        {rowCandidates.length === 0 ? (
                          <div className={MONO} style={{ color: 'var(--color-text-muted)' }}>
                            READ QUEUE — NONE STAGED · REVIEW SOURCE DOCS DIRECTLY
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setQueueOpen((q) => ({ ...q, [key]: !q[key] }))}
                              className={`${MONO} inline-flex items-center gap-1`}
                              style={{ color: 'var(--color-brand)' }}
                              data-testid={`read-queue-toggle-${key}`}
                            >
                              <span style={{ display: 'inline-block', transform: queueIsOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>▸</span>
                              READ QUEUE ({rowCandidates.length})
                            </button>
                            {queueIsOpen && (
                              <div className="mt-2 flex flex-col gap-2">
                                {rowCandidates.map((c, i) => {
                                  const ck = `${key}:${i}`;
                                  const qOpen = !!candQuoteOpen[ck];
                                  return (
                                    <div
                                      key={ck}
                                      className="rounded-[2px] px-2.5 py-2"
                                      style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface, #0a0a0a)' }}
                                      data-testid={`read-queue-item-${key}`}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="font-mono text-xs" style={{ color: 'var(--color-text-secondary)' }} title={c.source_label}>
                                          {midTruncate(c.source_label)}
                                        </span>
                                        {c.document_type && (
                                          <span className={MONO} style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                                            {c.document_type.replace(/_/g, ' ')}
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className="mt-1 text-xs"
                                        style={{
                                          color: 'var(--color-text-secondary)',
                                          fontFamily: "'DM Mono', monospace",
                                          display: qOpen ? undefined : '-webkit-box',
                                          WebkitLineClamp: qOpen ? undefined : 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: qOpen ? undefined : 'hidden',
                                        }}
                                      >
                                        “{c.quote}”
                                      </div>
                                      <div className="mt-1.5 flex items-center justify-between gap-2">
                                        <button
                                          className={MONO}
                                          style={{ color: 'var(--color-text-muted)' }}
                                          onClick={() => setCandQuoteOpen((q) => ({ ...q, [ck]: !q[ck] }))}
                                        >
                                          {qOpen ? 'COLLAPSE' : 'FULL QUOTE'}
                                        </button>
                                        <button
                                          onClick={() => useCandidate(key, c)}
                                          className={`rounded-[2px] px-2 py-1 ${MONO}`}
                                          style={{ border: '1px solid var(--color-border)', color: 'var(--color-brand)' }}
                                          data-testid={`use-cite-${key}`}
                                        >
                                          Use this cite →
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className={MONO} style={{ color: 'var(--color-text-muted)' }}>
                                  STAGED FOR REVIEW ONLY · PAGE IS SET WHEN YOU OPEN THE DOC · NO CANDIDATE CONCEDES
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {attested && (
                      <div className="mt-2 flex flex-col gap-1.5">
                        <span
                          className="inline-flex items-center gap-1 self-start rounded-[2px] px-2 py-0.5 font-mono text-xs uppercase tracking-wider"
                          style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                        >
                          <span style={{ color: 'var(--color-text-primary)' }}>✓</span>
                          {stipulated ? `STIPULATED ORDER · ${lever?.attestor?.id ?? ''}` : 'CONCEDED · ACCOUNTANT'}
                        </span>
                        {cite ? (
                          <div className="flex items-start gap-2">
                            <span className={MONO} style={{ color: 'var(--color-text-muted)' }}>CITE</span>
                            <span className="font-mono text-xs" style={{ color: 'var(--color-text-primary)' }}>
                              {cite.doc_id} · P. {cite.page}
                            </span>
                          </div>
                        ) : (
                          <span className={MONO} style={{ color: 'var(--color-text-muted)' }}>CITE — LOADING</span>
                        )}
                        {cite?.quote && (
                          <div className="pl-2" style={{ borderLeft: '1px solid var(--color-border)' }}>
                            <div
                              className="text-xs"
                              style={{
                                color: 'var(--color-text-secondary)',
                                display: quoteOpen[key] ? undefined : '-webkit-box',
                                WebkitLineClamp: quoteOpen[key] ? undefined : 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: quoteOpen[key] ? undefined : 'hidden',
                              }}
                            >
                              {cite.quote}
                            </div>
                            <button
                              className={`mt-0.5 ${MONO}`}
                              style={{ color: 'var(--color-text-muted)' }}
                              onClick={() => setQuoteOpen((q) => ({ ...q, [key]: !q[key] }))}
                            >
                              {quoteOpen[key] ? 'COLLAPSE' : 'FULL QUOTE'}
                            </button>
                          </div>
                        )}
                        {rowError[key] && (
                          <span className="text-xs" style={{ color: 'var(--color-blocker)' }}>{rowError[key]}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Col 2 — Cᵢ impact (read-only) */}
                  <div className="text-right">
                    {ci > 0 ? (
                      <>
                        {/* parentheses for all deduction amounts (K3 nit 5) — muted = hypothetical, red = proven */}
                        <div className="font-mono text-sm tabular-nums" style={{ color: attested ? 'var(--color-blocker)' : 'var(--color-text-muted)' }}>
                          {`(${fmtMoney(ci)})`}
                        </div>
                        <div className={`mt-0.5 ${MONO}`} style={{ color: attested ? 'var(--color-blocker)' : 'var(--color-text-muted)' }}>
                          {attested ? 'PROVEN' : 'IF PROVEN'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-mono text-sm" style={{ color: 'var(--color-text-muted)' }}>—</div>
                        <div className={`mt-0.5 ${MONO}`} style={{ color: 'var(--color-text-muted)' }}>NO DELTA</div>
                      </>
                    )}
                  </div>

                  {/* Col 3 — action */}
                  <div className="flex justify-end">
                    {!attested && !isComposerOpen && (
                      <button
                        onClick={() => openComposer(key)}
                        className={`rounded-[2px] px-3 py-1.5 ${MONO} transition-colors`}
                        style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-brand)';
                          e.currentTarget.style.color = 'var(--color-brand)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--color-border)';
                          e.currentTarget.style.color = 'var(--color-text-secondary)';
                        }}
                      >
                        Concede
                      </button>
                    )}
                    {/* composer-open: no col-3 cancel — one CANCEL lives beside CONCEDE below (K3 nit 6) */}
                    {attested && (
                      <button
                        onClick={() => handleArmedButton(key, 'clear')}
                        onBlur={() => clearArmed && disarm()}
                        disabled={busy}
                        className={`rounded-[2px] px-3 py-1.5 ${MONO} tabular-nums whitespace-nowrap`}
                        style={
                          clearArmed
                            ? { background: 'var(--color-blocker)', color: 'var(--color-surface, #0a0a0a)' }
                            : { border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }
                        }
                      >
                        {busy ? 'CLEARING…' : clearArmed ? `CONFIRM CLEAR · ${countdown}` : 'Clear'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Concede composer (spans full width) */}
                {isComposerOpen && !attested && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
                    <div className={MONO} style={{ color: 'var(--color-text-muted)' }}>CITATION — REQUIRED TO CONCEDE</div>
                    {prefillNote && (
                      <div
                        className="mt-2 rounded-[2px] px-2 py-1.5 text-xs"
                        style={{ border: '1px solid var(--color-border)', fontFamily: "'DM Mono', monospace" }}
                        data-testid="prefill-note"
                      >
                        {/* accent reserved for actions — provenance note earns hierarchy via
                            weight/opacity: lead-in full, doc + instruction dimmed (K3 gate) */}
                        <span style={{ color: 'var(--color-text-primary)' }}>PREFILLED FROM READ QUEUE</span>
                        <span style={{ color: 'var(--color-text-muted)' }}> · {midTruncate(prefillNote)} · CONFIRM THE PAGE ON THE DOCUMENT BEFORE YOU CONCEDE</span>
                      </div>
                    )}
                    <div className="mt-2 flex gap-3">
                      <div className="flex-1">
                        <input
                          value={fields.docId}
                          onChange={(e) => setFields((s) => ({ ...s, docId: e.target.value }))}
                          placeholder="e.g. EXH-041"
                          className="w-full rounded-[2px] px-2 py-1.5 font-mono text-sm"
                          style={{ background: 'var(--color-surface, #0a0a0a)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                        />
                        {fieldErrors.docId && <div className="mt-0.5 text-xs" style={{ color: 'var(--color-blocker)' }}>{fieldErrors.docId}</div>}
                      </div>
                      <div className="w-24">
                        <input
                          value={fields.page}
                          onChange={(e) => setFields((s) => ({ ...s, page: e.target.value.replace(/[^0-9]/g, '') }))}
                          placeholder="PG"
                          inputMode="numeric"
                          className="w-full rounded-[2px] px-2 py-1.5 font-mono text-sm text-right"
                          style={{ background: 'var(--color-surface, #0a0a0a)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                        />
                        {fieldErrors.page && <div className="mt-0.5 text-xs" style={{ color: 'var(--color-blocker)' }}>{fieldErrors.page}</div>}
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className={MONO} style={{ color: 'var(--color-text-muted)' }}>PULLED QUOTE — OPTIONAL</div>
                      <textarea
                        value={fields.quote}
                        onChange={(e) => setFields((s) => ({ ...s, quote: e.target.value }))}
                        rows={2}
                        maxLength={500}
                        className="mt-1 w-full rounded-[2px] px-2 py-1.5 text-sm"
                        style={{ background: 'var(--color-surface, #0a0a0a)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', fontFamily: "'DM Mono', monospace" }}
                      />
                    </div>
                    <div className={`mt-1 ${MONO}`} style={{ color: 'var(--color-text-muted)' }}>
                      CITATION IS THE ACT — DOC ID + PAGE REQUIRED.
                    </div>

                    {/* board-move preview */}
                    <div className="mt-2 text-right font-mono text-xs tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                      {ci > 0 && nValue != null ? (
                        <>
                          ON CONFIRM — E[X]:{' '}
                          <span style={{ color: 'var(--color-text-secondary)' }}>{fmtMoney(nValue)}</span>
                          {' → '}
                          <span style={{ color: 'var(--color-text-primary)' }}>{fmtMoney(nValue - ci)}</span>{'  '}
                          <span style={{ color: 'var(--color-blocker)' }}>({fmtMoney(ci)})</span>
                        </>
                      ) : (
                        'ON CONFIRM — E[X]: UNCHANGED —'
                      )}
                    </div>

                    {composerError && (
                      <div
                        className="mt-2 rounded-[2px] px-2 py-1.5 text-xs"
                        style={{ background: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)', color: 'var(--color-blocker)' }}
                      >
                        {composerError}
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-end gap-3">
                      <button onClick={cancelComposer} className={MONO} style={{ color: 'var(--color-text-muted)' }}>
                        Cancel
                      </button>
                      <button
                        onClick={() => composerValid && handleArmedButton(key, 'attest')}
                        onBlur={() => attestArmed && disarm()}
                        disabled={!composerValid || busy}
                        aria-disabled={!composerValid || busy}
                        title={!composerValid ? 'Doc id and page required' : undefined}
                        className={`rounded-[2px] px-3 py-1.5 ${MONO} tabular-nums`}
                        style={
                          !composerValid || busy
                            ? { border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', opacity: 0.4 }
                            // dark ink on orange in both valid states (K3 nit 1 — ~7.4:1 vs white's 2.9:1)
                            : { background: 'var(--color-brand)', color: 'var(--color-surface, #0b0b0c)' }
                        }
                      >
                        {busy ? 'RECORDING…' : attestArmed ? `CONFIRM CONCEDE · ${countdown}` : 'Concede'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── C. Footer ── */}
        {/* disclaimer grouped as one block: ~16px above the hairline via the last row's
            py-4, then pt-4/pb-4 inside; two lines share tight leading so they read as one
            legal note, not two stray captions (K3 nit 5) */}
        <div className="px-5 pt-4 pb-4" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)', fontFamily: "'DM Mono', monospace" }}>
            ENGINE DELTAS (Cᵢ) ARE READ-ONLY. CONCEDE SETS p=1 · CLEAR SETS p=0.<br />
            EVERY CONCEDE AND CLEAR IS WRITTEN TO THE AUDIT LOG WITH ITS CITATION.
          </div>
        </div>
      </div>
    </div>
  );
}
